import { ChevronDown, ChevronRight, ClipboardList } from "lucide-react"
import { useMemo, useState } from "react"

export interface ClinicalHistoryEntry {
  date: string
  type: string
  title?: string
  data: Record<string, unknown>
  display_fields: (string | Record<string, string>)[]
}

const STORAGE_KEY = "clinical-history-expanded"

function getInitialExpanded(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1"
  } catch {
    return false
  }
}

function formatDate(raw: string): string {
  try {
    const d = new Date(raw)
    if (Number.isNaN(d.getTime())) return raw
    return d.toLocaleDateString("en-GB", { month: "short", day: "2-digit", year: "2-digit" })
  } catch {
    return raw
  }
}

function EntryRow({ entry }: { entry: ClinicalHistoryEntry }) {
  const fields = entry.display_fields ?? []
  const data = entry.data ?? {}

  const resolved = fields.map((field) => {
    if (typeof field === "string") return { key: field, label: field }
    const key = Object.keys(field)[0]
    if (!key) return null
    return { key, label: field[key] ?? key }
  })

  return (
    <div className="relative pl-3.5 pb-2">
      <div className="absolute left-0 top-[5px] size-1.5 rounded-full bg-muted-foreground/40 ring-1 ring-background" />
      <div className="flex items-baseline gap-1.5">
        <span className="text-[10px] text-muted-foreground font-mono tabular-nums shrink-0">
          {formatDate(entry.date)}
        </span>
        <span className="text-[10px] font-medium text-foreground truncate">{entry.title ?? entry.type}</span>
      </div>
      {resolved.length > 0 && (
        <div className="mt-0.5 space-y-0">
          {resolved
            .filter((field) => field !== null)
            .map(({ key, label }) => {
              const value = data[key]
              if (value == null) return null
              return (
                <div key={key} className="text-[10px] text-muted-foreground leading-tight">
                  <span className="font-medium">{label}:</span>{" "}
                  <span className="text-foreground/80">{String(value)}</span>
                </div>
              )
            })}
        </div>
      )}
    </div>
  )
}

export function ClinicalHistoryPanel({
  clinicalHistory
}: {
  clinicalHistory: ClinicalHistoryEntry[] | null | undefined
}) {
  const [expanded, setExpanded] = useState(getInitialExpanded)

  const entries = useMemo(() => {
    const parsed = Array.isArray(clinicalHistory) ? clinicalHistory : []
    return [...parsed].sort((a, b) => {
      const da = new Date(a.date).getTime()
      const db = new Date(b.date).getTime()
      if (Number.isNaN(da) && Number.isNaN(db)) return 0
      if (Number.isNaN(da)) return 1
      if (Number.isNaN(db)) return -1
      return db - da
    })
  }, [clinicalHistory])

  if (entries.length === 0) return null

  const toggle = () => {
    const next = !expanded
    setExpanded(next)
    try {
      localStorage.setItem(STORAGE_KEY, next ? "1" : "0")
    } catch {
      // ignore
    }
  }

  return (
    <div className="border-t border-border shrink-0">
      <button
        type="button"
        onClick={toggle}
        className="flex items-center gap-1.5 w-full px-3 py-1.5 hover:bg-muted/50 transition-colors"
      >
        <ClipboardList className="size-3 text-muted-foreground shrink-0" />
        <span className="text-[10px] font-semibold text-muted-foreground flex-1 text-left">Clinical History</span>
        {expanded ? (
          <ChevronDown className="size-3 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-3 text-muted-foreground" />
        )}
      </button>
      {expanded && (
        <div className="max-h-[200px] overflow-y-auto px-3 pb-1.5">
          <div className="relative border-l border-border/50 ml-0.5">
            {entries.map((entry, i) => (
              <EntryRow key={`${entry.date}-${entry.type}-${i}`} entry={entry} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
